/**
 * Error reporting for browser-to-backend calls.
 *
 * `fetch` reports every network-layer refusal as the same opaque
 * `TypeError: Failed to fetch` - CORS, mixed content, DNS, a stopped server.
 * Calling a redesign backend directly from the page makes that the single most
 * likely failure, and "Failed to fetch" tells the user nothing actionable. These
 * helpers turn the situation into a specific hint.
 */

export class RedesignError extends Error {
  constructor(
    message: string,
    /** What to actually do about it. */
    readonly hint?: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = 'RedesignError';
  }
}

/** Diagnoses a failed `fetch` against the URL it was aimed at. */
export function describeFetchFailure(cause: unknown, url: string): RedesignError {
  const pageIsSecure =
    typeof location !== 'undefined' && location.protocol === 'https:';
  const targetIsPlain = url.startsWith('http://');

  if (pageIsSecure && targetIsPlain) {
    return new RedesignError(
      `Cannot reach ${url}`,
      'The workbench is on https and the backend is on http, so the browser blocks the request as mixed content. Serve the backend over https, or run the workbench over http for local work.',
      { cause },
    );
  }

  return new RedesignError(
    `Cannot reach ${url}`,
    'Usually CORS: the browser calls the backend directly, so it must send Access-Control-Allow-Origin for this page. Self-hosted ComfyUI needs --enable-cors-header; a managed host may need the origin allow-listed. Check the browser console for the blocked request, and that the backend is actually up.',
    { cause },
  );
}

/** Turns a non-2xx response into an error carrying the body, which usually explains why. */
export async function describeHttpFailure(
  response: Response,
  what: string,
): Promise<RedesignError> {
  let detail = '';
  try {
    detail = (await response.text()).slice(0, 600);
  } catch {
    // Body already consumed or unreadable; the status alone will have to do.
  }
  const hint =
    response.status === 401 || response.status === 403
      ? 'The API key was rejected or is missing.'
      : response.status === 404
        ? 'Check the endpoint path - a ComfyUI base URL should not include /prompt.'
        : response.status === 413
          ? 'The image is too large for this endpoint; export at a smaller max edge.'
          : undefined;

  return new RedesignError(
    `${what} failed: ${response.status} ${response.statusText}${detail ? ` - ${detail}` : ''}`,
    hint,
  );
}

/** `fetch` with abort support and the diagnostics above. */
export async function request(
  url: string,
  init: RequestInit,
  what: string,
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw describeFetchFailure(cause, url);
  }
  if (!response.ok) throw await describeHttpFailure(response, what);
  return response;
}
